import React, { useMemo, useState } from "react";
import { Droplets, Zap, Save, CheckCircle2, MessageSquare, Wallet, MoreVertical, Trash2 } from "lucide-react";
import { useData } from "../context/DataContext";
import Select from "../components/Select";
import ConfirmDialog from "../components/ConfirmDialog";
import DropdownMenu from "../components/DropdownMenu";
import RecordPaymentDialog from "../components/RecordPaymentDialog";
import UtilitySmsDialog from "../components/UtilitySmsDialog";
import BulkUtilitySmsDialog from "../components/BulkUtilitySmsDialog";
import {
  currentMonth,
  monthOptions,
  formatMonth,
  formatKES,
  utilityChargeForMonth,
  utilityBalanceForMonth,
  paidForMonthByType,
  computeTenantStatus,
} from "../utils/format";

export default function UtilityBills() {
  const { properties, tenants, payments, utilityCharges, addUtilityCharge, voidMonthPayments } = useData();
  const [month, setMonth] = useState(currentMonth());
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [drafts, setDrafts] = useState({});
  const [saved, setSaved] = useState(new Set());
  const [paymentFor, setPaymentFor] = useState(null);
  const [smsFor, setSmsFor] = useState(null);
  const [bulkSmsOpen, setBulkSmsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [voidConfirm, setVoidConfirm] = useState(null); // { tenantId, tenantName, type }
  const [voiding, setVoiding] = useState(false);

  const STATUS_OPTIONS = [
    { value: "all", label: "All statuses" },
    { value: "paid", label: "Paid" },
    { value: "partial", label: "Partial" },
    { value: "pending", label: "Pending" },
    { value: "overdue", label: "Overdue" },
  ];

  const utilityProperties = properties.filter(
    (p) => p.waterEnabled || p.electricityEnabled
  );

  const propertyOptions = [
    { value: "all", label: "All properties" },
    ...utilityProperties.map((p) => ({ value: p.id, label: p.name })),
  ];

  // Get tenants with their property info, filtered by property and payment status.
  const rows = useMemo(() => {
    return tenants
      .filter((t) => t.status === "ACTIVE")
      .filter((t) => {
        const prop = properties.find((p) => p.id === t.propertyId);
        if (!prop?.waterEnabled && !prop?.electricityEnabled) return false;
        if (propertyFilter !== "all" && t.propertyId !== propertyFilter) return false;
        return true;
      })
      .filter((t) => {
        if (statusFilter === "all") return true;
        return computeTenantStatus(t, payments, month).toLowerCase() === statusFilter;
      })
      .map((t) => ({
        tenant: t,
        property: properties.find((p) => p.id === t.propertyId),
      }));
  }, [tenants, properties, payments, month, propertyFilter, statusFilter]);

  const getVal = (tenantId, type) => {
    const key = `${tenantId}-${month}-${type}`;
    if (drafts[key] !== undefined) return drafts[key];
    const existing = utilityChargeForMonth(utilityCharges, tenantId, month, type);
    return existing > 0 ? String(existing) : "";
  };

  const setVal = (tenantId, type, val) => {
    const key = `${tenantId}-${month}-${type}`;
    setDrafts((prev) => ({ ...prev, [key]: val }));
    setSaved((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const saveOne = async (tenantId, type) => {
    const key = `${tenantId}-${month}-${type}`;
    const val = Number(getVal(tenantId, type)) || 0;
    try {
      await addUtilityCharge({ tenantId, month, type, amount: val });
      setSaved((prev) => new Set(prev).add(key));
    } catch {
      // silent — input stays unsaved
    }
  };

  const saveAll = () => {
    rows.forEach(({ tenant: t, property: p }) => {
      if (p?.waterEnabled) saveOne(t.id, "water");
      if (p?.electricityEnabled) saveOne(t.id, "electricity");
    });
  };

  const changeMonth = (m) => {
    setMonth(m);
    setDrafts({});
    setSaved(new Set());
  };

  // Any columns needed across the filtered set.
  const showWater = rows.some((r) => r.property?.waterEnabled);
  const showElec = rows.some((r) => r.property?.electricityEnabled);

  if (utilityProperties.length === 0) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Utilities</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Enter monthly water and electricity bills for tenants.
          </p>
        </div>
        <div className="card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
            <Droplets className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
            No properties with utilities enabled
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Enable water or electricity billing on a property first (Properties → Edit property).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Utilities</h2>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              {rows.length} tenant{rows.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Enter utility bills for {formatMonth(month)}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="w-44"
            value={propertyFilter}
            onChange={setPropertyFilter}
            options={propertyOptions}
          />
          <Select
            className="w-36"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
          <Select
            className="w-44"
            value={month}
            onChange={changeMonth}
            options={monthOptions()}
          />
          <button onClick={saveAll} className="btn-primary text-sm">
            <Save className="h-4 w-4" />
            Save all
          </button>
          <button
            onClick={() => setBulkSmsOpen(true)}
            disabled={rows.length === 0}
            className="btn-secondary text-sm"
          >
            <MessageSquare className="h-4 w-4" />
            Bulk SMS
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No active tenants with utilities enabled.
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <Th>Tenant</Th>
                  <Th>Property · House</Th>
                  {showWater && <Th>Water (KES)</Th>}
                  {showElec && <Th>Electricity (KES)</Th>}
                  <Th>Status</Th>
                  <Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {rows.map(({ tenant: t, property: p }) => {
                  const waterPaid = paidForMonthByType(payments, t.id, month, "water");
                  const elecPaid = paidForMonthByType(payments, t.id, month, "electricity");
                  const waterCharge = utilityChargeForMonth(utilityCharges, t.id, month, "water");
                  const elecCharge = utilityChargeForMonth(utilityCharges, t.id, month, "electricity");
                  return (
                    <tr key={t.id}>
                      <Td className="font-medium text-gray-900 dark:text-white">
                        {t.name}
                        <div className="text-xs font-normal text-gray-500 dark:text-gray-400">{t.phone || "—"}</div>
                      </Td>
                      <Td>
                        {p?.name || "—"} · {t.houseNumber}
                      </Td>
                      {showWater && (
                        <Td>
                          {p?.waterEnabled ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="number"
                                  min="0"
                                  className="input-base w-28 text-right"
                                  value={getVal(t.id, "water")}
                                  onChange={(e) => setVal(t.id, "water", e.target.value)}
                                  onBlur={() => saveOne(t.id, "water")}
                                  placeholder="0"
                                />
                                {saved.has(`${t.id}-${month}-water`) && (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                )}
                              </div>
                              {waterPaid > 0 && (
                                <div className="mt-0.5 text-[11px] text-green-600">{formatKES(waterPaid)} paid</div>
                              )}
                              {waterCharge > 0 && waterPaid < waterCharge && (
                                <div className="mt-0.5 text-[11px] text-red-500">{formatKES(waterCharge - waterPaid)} due</div>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </Td>
                      )}
                      {showElec && (
                        <Td>
                          {p?.electricityEnabled ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="number"
                                  min="0"
                                  className="input-base w-28 text-right"
                                  value={getVal(t.id, "electricity")}
                                  onChange={(e) => setVal(t.id, "electricity", e.target.value)}
                                  onBlur={() => saveOne(t.id, "electricity")}
                                  placeholder="0"
                                />
                                {saved.has(`${t.id}-${month}-electricity`) && (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                )}
                              </div>
                              {elecPaid > 0 && (
                                <div className="mt-0.5 text-[11px] text-green-600">{formatKES(elecPaid)} paid</div>
                              )}
                              {elecCharge > 0 && elecPaid < elecCharge && (
                                <div className="mt-0.5 text-[11px] text-red-500">{formatKES(elecCharge - elecPaid)} due</div>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </Td>
                      )}
                      <Td>
                        <div className="space-y-1">
                          {p?.waterEnabled && waterCharge > 0 && (
                            <UtilityBadge label="Water" icon={Droplets} paid={waterPaid >= waterCharge} partial={waterPaid > 0 && waterPaid < waterCharge} />
                          )}
                          {p?.electricityEnabled && elecCharge > 0 && (
                            <UtilityBadge label="Elec" icon={Zap} paid={elecPaid >= elecCharge} partial={elecPaid > 0 && elecPaid < elecCharge} />
                          )}
                          {(!p?.waterEnabled || !waterCharge) && (!p?.electricityEnabled || !elecCharge) && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">No bills</span>
                          )}
                        </div>
                      </Td>
                      <Td className="text-right">
                        <DropdownMenu
                          align="right"
                          trigger={(toggle) => (
                            <button
                              onClick={toggle}
                              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                              aria-label="Actions"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          )}
                          items={[
                            { label: "Send SMS", icon: MessageSquare, onClick: () => setSmsFor(t) },
                            { label: "Record payment", icon: Wallet, onClick: () => setPaymentFor(t) },
                            ...(waterPaid > 0
                              ? [{ label: "Void water payment", icon: Trash2, tone: "danger", onClick: () => setVoidConfirm({ tenantId: t.id, tenantName: t.name, type: "water" }) }]
                              : []),
                            ...(elecPaid > 0
                              ? [{ label: "Void electricity payment", icon: Trash2, tone: "danger", onClick: () => setVoidConfirm({ tenantId: t.id, tenantName: t.name, type: "electricity" }) }]
                              : []),
                          ]}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-500 dark:bg-gray-900 dark:text-gray-400">
        Bills auto-save when you leave a field, or click "Save all" to save everything at once.
      </div>

      <RecordPaymentDialog
        open={!!paymentFor}
        onClose={() => setPaymentFor(null)}
        tenant={paymentFor}
        defaultMonth={month}
        allowedTypes={["water", "electricity"]}
      />

      <UtilitySmsDialog
        open={!!smsFor}
        onClose={() => setSmsFor(null)}
        tenant={smsFor}
      />

      <BulkUtilitySmsDialog
        open={bulkSmsOpen}
        onClose={() => setBulkSmsOpen(false)}
        tenants={rows.map((r) => r.tenant)}
      />

      <ConfirmDialog
        open={!!voidConfirm}
        onClose={() => !voiding && setVoidConfirm(null)}
        title={`Void ${voidConfirm?.type} payment?`}
        description={`This will remove all ${voidConfirm?.type} payments for ${voidConfirm?.tenantName} in ${formatMonth(month)}. This cannot be undone.`}
        confirmLabel={voiding ? "Voiding…" : "Void payment"}
        cancelLabel="Cancel"
        tone="danger"
        loading={voiding}
        onConfirm={async () => {
          if (!voidConfirm) return;
          setVoiding(true);
          try {
            await voidMonthPayments(voidConfirm.tenantId, month, voidConfirm.type);
          } finally {
            setVoiding(false);
            setVoidConfirm(null);
          }
        }}
      />
    </div>
  );
}

function UtilityBadge({ label, icon: Icon, paid, partial }) {
  const cls = paid
    ? "bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-950 dark:text-green-400 dark:ring-green-500/30"
    : partial
    ? "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-950 dark:text-blue-400 dark:ring-blue-500/30"
    : "bg-amber-50 text-amber-700 ring-amber-600/20 dark:bg-amber-950 dark:text-amber-400 dark:ring-amber-500/30";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${cls}`}>
      <Icon className="h-3 w-3" />
      {label}: {paid ? "Paid" : partial ? "Partial" : "Unpaid"}
    </span>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`whitespace-nowrap px-5 py-3 text-sm text-gray-700 dark:text-gray-300 ${className}`}>
      {children}
    </td>
  );
}
